import { createAction, createReducer, on, props } from "@ngrx/store";
import { WorkflowItemDto } from "../models";

// Existing actions
/** Action: request a load of the current user's workflow items. */
export const workflowItemsLoad = createAction('[Workflow] load workflow items');
/** Action: workflow items successfully loaded from the API. */
export const workflowItemsLoadedFromApi = createAction(
    '[Workflow] workflow items loaded from api', props<{ workflowItems: WorkflowItemDto[] }>()
);
/** Action: workflow items load failed. */
export const workflowItemsLoadedFromApiFailed = createAction(
    '[Workflow] workflow items load from api failed'
);

/** Action: request a load of all workflow group names. */
export const workflowGroupsLoad = createAction('[Workflow] load workflow groups');
/** Action: workflow groups successfully loaded from the API. */
export const workflowGroupsLoadedFromApi = createAction(
    '[Workflow] workflow groups loaded from api', props<{ workflowGroups: string[] }>()
);
/** Action: workflow groups load failed. */
export const workflowGroupsLoadedFromApiFailed = createAction(
    '[Workflow] workflow groups load from api failed'
);

// New actions for obtaining a single workflow item by ID
/** Action: request a load of a single workflow item by id. */
export const workflowItemLoad = createAction(
    '[Workflow] load workflow item',
    props<{ id: string }>()
);
/** Action: a single workflow item was successfully loaded from the API. */
export const workflowItemLoadedFromApi = createAction(
    '[Workflow] workflow item loaded from api',
    props<{ workflowItem: WorkflowItemDto }>()
);
/** Action: single workflow item load failed. */
export const workflowItemLoadedFromApiFailed = createAction(
    '[Workflow] workflow item load from api failed'
);

/** Action: update a workflow item locally after a user action, optionally moving it between groups. */
export const workflowUpdateItem = createAction('[Workflow] update workflow item', 
    props<{ workflowItem: WorkflowItemDto, previousGroup?: string }>());

// Existing state and reducer
/**
 * ngrx state for the workflow store: known group names and items bucketed by assigned group.
 */
export interface WorkflowState {
    workflowGroups: string[],
    workflowItems: { [key: string]: WorkflowItemDto[] }
}

const initialState: WorkflowState = {
    workflowGroups: [],
    workflowItems: {}
};

/**
 * Reducer for the workflow store. Buckets loaded items by assigned group, merges
 * single-item loads into the correct group and moves items between groups on update.
 */
export const workflowReducer = createReducer(initialState,
    on(workflowItemsLoadedFromApi, (state, { workflowItems }) => ({
        workflowItems: workflowItems.reduce((acc, item) => {
            const group = item.assignedGroup;
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(item);
            return acc;
        }, {} as { [key: string]: WorkflowItemDto[] }),
        workflowGroups: state.workflowGroups
    })),
    on(workflowGroupsLoadedFromApi, (state, { workflowGroups }) => {
        return {
        ...state,
        workflowGroups
        }
    }),
    on(workflowItemLoadedFromApi, (state, { workflowItem }) => {
        const group = workflowItem.assignedGroup;
        const workItems = { ...state.workflowItems };

        // Ensure the group exists in the workItems object
        if (!workItems[group]) {
            workItems[group] = [];
        }

        // Create a new array for the group to avoid mutating the existing array
        const updatedGroupItems = [...workItems[group]];

        const existingIndex = updatedGroupItems.findIndex(item => item.workflowItemId === workflowItem.workflowItemId);
        if (existingIndex !== -1) {
            // Update the existing item
            updatedGroupItems[existingIndex] = workflowItem;
        } else {
            // Add the new item
            updatedGroupItems.push(workflowItem);
        }

        // Return a new state with the updated workItems object
        return {
            ...state,
            workflowItems: {
                ...workItems,
                [group]: updatedGroupItems
            }
        };
    }),
    on(workflowUpdateItem, (state, { workflowItem, previousGroup }) => {
        const newGroup = workflowItem.assignedGroup;
        const workItems = { ...state.workflowItems };

        var updatedPreviousGroupItems = [...workItems[previousGroup] ?? []];

        // Remove the item from the previous group
        if (previousGroup) {
            updatedPreviousGroupItems= updatedPreviousGroupItems.filter(
                item => item.workflowItemId !== workflowItem.workflowItemId
            );
        }

        // Add or update the item in the new group
        if (!workItems[newGroup]) {
            workItems[newGroup] = [];
        }
        const updatedNewGroupItems = [...workItems[newGroup]];
        const existingIndex = updatedNewGroupItems.findIndex(
            item => item.workflowItemId === workflowItem.workflowItemId
        );
        if (existingIndex !== -1) {
            // Update the existing item
            updatedNewGroupItems[existingIndex] = workflowItem;
        } else {
            // Add the new item
            updatedNewGroupItems.push(workflowItem);
        }

        // Update the state immutably
        return {
            ...state,
            workflowItems: {
                ...workItems,
                [previousGroup]: updatedPreviousGroupItems,
                [newGroup]: updatedNewGroupItems
            }
        };
    })
);